import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bookmark, Calendar, Globe2, MapPin, Users } from 'lucide-react';
import { useLang } from '../../i18n';
import { buildStay22EmbedUrl, buildStay22SearchUrl, trackStay22, useStay22Config } from '../../config/stay22';

const inputCls = 'eco-input';

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="eco-field-label mb-1.5 block">{label}</span>
    {children}
  </label>
);

export const Stay22Block = ({
  destination = '',
  lat = null,
  lng = null,
  campaign = 'eco_accommodations',
  showMap = true,
  embedId = '',
  testid = 'stay22-block',
}) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const config = useStay22Config();
  const [dest, setDest] = useState(destination);
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [adults, setAdults] = useState('2');
  const [children, setChildren] = useState('0');
  const [error, setError] = useState('');
  const startedRef = useRef(false);
  const today = new Date().toISOString().split('T')[0];

  if (!config || !config.enabled) return null;

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackStay22({ surface: 'search_started', destination: dest || destination, campaign });
  };

  const submit = (e) => {
    e.preventDefault();
    const target = dest.trim();
    if (!target && lat == null) {
      setError(t('s22.invalid'));
      return;
    }
    if ((checkin && !checkout) || (!checkin && checkout) || (checkin && checkout && checkout <= checkin)) {
      setError(t('s22.invalid'));
      return;
    }
    setError('');
    trackStay22({ surface: 'outbound_click', destination: target, checkin, checkout, adults: Number(adults), children: Number(children), campaign });
    window.open(
      buildStay22SearchUrl({ aid: config.aid, address: target, checkin, checkout, adults, children, campaign }),
      '_blank',
      'noopener'
    );
  };

  const saveStay = () => {
    trackStay22({ surface: 'save_stay', destination: dest || destination, checkin, checkout, adults: Number(adults), children: Number(children), campaign });
    const q = new URLSearchParams();
    if (destination) q.set('destination', destination);
    const noteBits = [`Alojamento externo (Stay22): ${dest || destination}`];
    if (checkin && checkout) noteBits.push(`${checkin} → ${checkout}`);
    noteBits.push(`${adults} adulto(s), ${children} criança(s)`);
    q.set('notes', noteBits.join(' · '));
    navigate(`/plan-my-trip?${q.toString()}`);
  };

  const embedSrc = buildStay22EmbedUrl({
    embedId: embedId || config.embed_id,
    aid: config.aid,
    address: dest || destination,
    lat,
    lng,
    checkin,
    checkout,
    adults: Number(adults),
    children: Number(children),
    campaign,
  });

  return (
    <div data-testid={testid} className="rounded-3xl border border-eco-ink/10 bg-white p-6 shadow-sm md:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <Globe2 size={18} className="shrink-0 text-eco-gold" />
        <h3 className="text-xl leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink" data-testid={`${testid}-title`}>{t('s22.more.t')}</h3>
        <span className="rounded-full bg-eco-gold/15 px-3 py-1 text-xs font-semibold text-eco-gold" data-testid={`${testid}-badge`}>{t('s22.badge')}</span>
      </div>
      <p className="eco-body mt-2 text-eco-ink/60" data-testid={`${testid}-disclaimer`}>{t('s22.ext.s')}</p>

      <form onSubmit={submit} noValidate className="mt-5" data-testid={`${testid}-form`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label={t('search.destination')} testid={`${testid}-destination`}>
              <div className="relative">
                <MapPin size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-eco-ink/40" />
                <input
                  value={dest}
                  onFocus={markStarted}
                  onChange={(e) => setDest(e.target.value)}
                  placeholder="São Tomé, Príncipe, Cabo Verde…"
                  className={`${inputCls} pl-9`}
                  data-testid={`${testid}-destination-input`}
                />
              </div>
            </Field>
          </div>
          <Field label={t('s22.checkin')} testid={`${testid}-checkin`}>
            <div className="relative">
              <Calendar size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-eco-ink/40" />
              <input type="date" min={today} value={checkin} onFocus={markStarted} onChange={(e) => setCheckin(e.target.value)} className={`${inputCls} pl-9`} data-testid={`${testid}-checkin-input`} />
            </div>
          </Field>
          <Field label={t('s22.checkout')} testid={`${testid}-checkout`}>
            <div className="relative">
              <Calendar size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-eco-ink/40" />
              <input type="date" min={checkin || today} value={checkout} onFocus={markStarted} onChange={(e) => setCheckout(e.target.value)} className={`${inputCls} pl-9`} data-testid={`${testid}-checkout-input`} />
            </div>
          </Field>
          <Field label={t('form.adults')} testid={`${testid}-adults`}>
            <div className="relative">
              <Users size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-eco-ink/40" />
              <select value={adults} onChange={(e) => setAdults(e.target.value)} className={`${inputCls} pl-9`} data-testid={`${testid}-adults-select`}>
                {['1', '2', '3', '4', '5', '6', '7', '8'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </Field>
          <Field label={t('form.children')} testid={`${testid}-children`}>
            <div className="relative">
              <Users size={15} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-eco-ink/40" />
              <select value={children} onChange={(e) => setChildren(e.target.value)} className={`${inputCls} pl-9`} data-testid={`${testid}-children-select`}>
                {['0', '1', '2', '3', '4'].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </Field>
        </div>
        {error && <p data-testid={`${testid}-error`} className="mt-3 text-sm font-medium text-red-500">{error}</p>}
        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
          <button type="submit" data-testid={`${testid}-submit`} className="eco-btn-primary group flex-1 justify-center">
            {t('cta.checkstay')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button type="button" onClick={saveStay} data-testid={`${testid}-save`} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-[10px] border border-eco-ink/15 px-6 text-[15px] font-semibold text-eco-ink transition-colors duration-200 hover:border-eco-green hover:text-eco-green md:min-h-11">
            <Bookmark size={15} /> {t('s22.save')}
          </button>
        </div>
        <p className="mt-2.5 text-center text-xs text-eco-ink/50" data-testid={`${testid}-note`}>{t('s22.open')}</p>
      </form>

      {showMap && (
        <div className="mt-6">
          <p className="eco-field-label mb-2" data-testid={`${testid}-map-label`}>{t('s22.map.t')}</p>
          <iframe
            id="stay22-widget"
            data-testid={`${testid}-map`}
            title={t('s22.map.t')}
            src={embedSrc}
            width="100%"
            height="428"
            frameBorder="0"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            className="w-full rounded-2xl border border-eco-ink/10"
          />
        </div>
      )}
    </div>
  );
};
